import { TAG_TYPES } from '@/lib/api/tags';
import { processServerError, handledError } from '@/lib/api/listQuery';
import { notifyOnSuccess } from '@/lib/api/notifyOnSuccess';
import { buildFulfillmentFiltersInput } from '@/lib/mappers/filters';
import { buildDeclarationDto } from '../mappers/declarationDto';
import { declarationFileName } from '../constants/declarationDocumentFields';
import {
  processAllocationsPage,
  buildFulfillmentsQuery,
  processFulfillmentFilterOptions,
  processAllocationDetail,
  processAllocationAttachments,
  processAllocationDeclaration,
  buildAllocationShipmentDto,
} from '../mappers/allocationsMappers';

// The writes stay REST under the module segment; the list is GraphQL.
const PATHS = {
  detail: (module, id) => `/${module}/pending-orders/fulfillments/${id}`,
  revoke: (module, id) => `/${module}/pending-orders/fulfillments/${id}/revoke`,
  reject: (module, id) => `/${module}/transactions/${id}/reject`,
  // The rendered PDF is uploaded here first, then linked by the save below.
  declarationFile: (module, id) => `/${module}/transactions/${id}/declaration/file`,
  declaration: (module, id) => `/${module}/transactions/declaration/${id}`,
  orderShipments: (module, orderId) => `/${module}/pending-orders/${orderId}/shipments`,
  bulkGenerateDeclarations: (module) =>
    `/${module}/pending-orders/fulfillments/bulk-generate-declarations`,
};

// A write here changes the row's status, so the list and the row it came from both re-read.
const allocationTags = (result, error, arg) => [
  { type: TAG_TYPES.Allocation, id: arg.id },
  { type: TAG_TYPES.Allocation, id: 'LIST' },
];

// Revoke and reject hand the quantity back, so the order, shipment and Inventory totals re-read too.
const releasedAllocationTags = (result, error, arg) => [
  ...allocationTags(result, error, arg),
  TAG_TYPES.Order,
  TAG_TYPES.OutgoingShipment,
  TAG_TYPES.Inventory,
];

// Everything both the Allocations table and its detail header read; `Fulfillment` has 45+.
const FULFILLMENT_FIELDS = `
  fragment FulfillmentFields on Fulfillment {
    id
    transactionId
    approvalStatus
    transactionDate
    dateOfDispatch
    daysLeft
    massBalancePeriodLabel
    materialName
    declarationUnit
    workingUnit
    certifiedQuantityWorking
    certifiedQuantityDeclaration
    allocatedQuota
    siteId
    sentFrom
    receivedAt
    shippingTo
    shippingToSites
    terminal
    reference { incomingDeclarationRef orderNo shipmentNo pendingOrderId }
    associatedDeclarationNo
    declarationNo
    fileId
    requiredFieldsComplete
    canGenerateDeclaration
    canRevoke
    transactedBy { name email }
    ghgEmission
    ghgEmissionSavings
    rawMaterialType
    countryOfOrigin
    feedstockCategory
    vesselName
  }
`;

export const FULFILLMENTS_QUERY = `
  ${FULFILLMENT_FIELDS}
  query Fulfillments($certificateType: CertificateType, $limit: Int, $page: Int, $filters: FulfillmentFilters, $search: FulfillmentSearch) {
    fulfillments(certificateType: $certificateType, limit: $limit, page: $page, filters: $filters, search: $search) {
      nodes { ...FulfillmentFields }
      pageInfo { total }
    }
  }
`;

// The filter panel's Material and Send to choices — only what the table's allocations carry.
export const FULFILLMENT_FILTER_OPTIONS_QUERY = `
  query FulfillmentFilterOptions($certificateType: CertificateType, $filters: FulfillmentFilters) {
    fulfillmentFilterOptions(certificateType: $certificateType, filters: $filters) {
      materials { id name }
      customers { id name }
    }
  }
`;

// The detail header reads the same node; only its attachments tab asks for more.
export const FULFILLMENT_QUERY = `
  ${FULFILLMENT_FIELDS}
  query Fulfillment($id: ID!) {
    fulfillment(id: $id) { ...FulfillmentFields }
  }
`;

export const FULFILLMENT_ATTACHMENTS_QUERY = `
  query FulfillmentAttachments($id: ID!) {
    fulfillment(id: $id) {
      id
      attachments { fileId fileName attachmentType uploadedAt }
    }
  }
`;

// Generate/Edit declaration prefill — the server resolves supplier/recipient and the inbound's carried blocks.
export const ALLOCATION_DECLARATION_QUERY = `
  query AllocationDeclaration($id: ID!) {
    allocationDeclaration(id: $id) {
      id
      declarationFileId
      energyContent
      allocation { certifiedQuantity unit }
      declarationDetails {
        sustainabilityDeclarationNumber
        dateOfIssuance
        dateOfDispatch
        supplierName
        supplierAddress
        supplierCertificateNumber
        supplierAddressOfDispatch
        recipientName
        recipientAddress
        recipientContractNumber
        recipientAddressOfReceipt
      }
      materialsDetails {
        typeOfProduct
        typeOfRawMaterial
        additionalInformation
        countryOfOrigin
        isccCompliantMaterial
        euRedCompliantMaterial
        chainOfCustodyOption
        countryOfBiofuelProduction
        startDateOfBiofuelProduction
        startDateOfBioliquidUse
        rawMaterialSustainabilityCriteria
        biomassIntermediateCrop
        biomassIlucRiskFeedstocks
        rawMaterialWasteDefinition
        wasteAnimalByProductPermitNumber
        supportForProduction
        supportNatureAndScheme
      }
      emissionDetails {
        ghgEmissionRed3
        totalGhgEmissions
        ghgEec
        ghgEl
        ghgEp
        ghgEtd
        ghgEu
        ghgEsca
        ghgEccs
        ghgEccr
        allocatedHeat
        allocatedElectricity
        escaBonusCap
        nuts2Area
        biofuelsForTransport
        bioliquidsBiomassFuelsElectricity
        biomassFuelsElectricityOutermostRegions
        bioliquidsBiomassFuelsUsefulHeat
        biomassFuelsUsefulHeatCoalSubstitution
        fssFoodSecurityStandard
        eudrDeforestationRegulation
        nonGmoFoodFeed
        nonGmoTechnicalMarkets
        shippingDistanceKm
        trainDistanceKm
        roadDistanceKm
        disaggregatedDefaultValueForOil
        disaggregatedDefaultValueForSoil
        elBonusObtained
      }
    }
  }
`;

export const allocationsEndpoints = (build) => ({
  getAllocations: build.query({
    // `document` routes this through GraphQL; the module scopes it as `certificateType`.
    query: (screenState) => ({
      document: FULFILLMENTS_QUERY,
      variables: buildFulfillmentsQuery(screenState),
    }),
    transformResponse: (data) => processAllocationsPage(data?.fulfillments),
    providesTags: (result) =>
      result
        ? [
            ...result.rows.map((row) => ({ type: TAG_TYPES.Allocation, id: row.id })),
            { type: TAG_TYPES.Allocation, id: 'LIST' },
          ]
        : [{ type: TAG_TYPES.Allocation, id: 'LIST' }],
  }),

  // Takes the page's fixed scope only — a picked filter here would hide its siblings.
  getAllocationFilterOptions: build.query({
    query: ({ certificateType, extraFilters }) => ({
      document: FULFILLMENT_FILTER_OPTIONS_QUERY,
      variables: { certificateType, filters: buildFulfillmentFiltersInput({}, extraFilters) },
    }),
    transformResponse: (data) => processFulfillmentFilterOptions(data?.fulfillmentFilterOptions),
    providesTags: [{ type: TAG_TYPES.Allocation, id: 'LIST' }],
  }),

  getAllocationDetail: build.query({
    query: ({ id }) => ({ document: FULFILLMENT_QUERY, variables: { id } }),
    transformResponse: (data) => processAllocationDetail(data?.fulfillment),
    providesTags: (result, error, arg) => [{ type: TAG_TYPES.Allocation, id: arg.id }],
  }),

  // The PDF's declaration, and the file id Download hands over.
  getAllocationDeclaration: build.query({
    query: ({ id }) => ({ document: ALLOCATION_DECLARATION_QUERY, variables: { id } }),
    transformResponse: (data) => processAllocationDeclaration(data?.allocationDeclaration),
    transformErrorResponse: handledError,
    providesTags: (result, error, arg) => [{ type: TAG_TYPES.Allocation, id: arg.id }],
  }),

  getAllocationAttachments: build.query({
    query: ({ id }) => ({ document: FULFILLMENT_ATTACHMENTS_QUERY, variables: { id } }),
    transformResponse: (data) => processAllocationAttachments(data?.fulfillment?.attachments),
    providesTags: (result, error, arg) => [{ type: TAG_TYPES.Allocation, id: arg.id }],
  }),

  revokeAllocation: build.mutation({
    query: ({ module, id }) => ({ url: PATHS.revoke(module, id), method: 'POST' }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('ALLOCATIONS_REVOKE_SUCCESS'),
    invalidatesTags: releasedAllocationTags,
  }),

  rejectAllocation: build.mutation({
    query: ({ module, id }) => ({ url: PATHS.reject(module, id), method: 'POST' }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('ALLOCATIONS_REJECT_SUCCESS'),
    invalidatesTags: releasedAllocationTags,
  }),

  // Update shipment details — one shipment per card; the server links it and advances the allocation.
  createAllocationShipment: build.mutation({
    query: ({ module, orderId, shipment, unitByType }) => ({
      url: PATHS.orderShipments(module, orderId),
      method: 'POST',
      body: buildAllocationShipmentDto(shipment, unitByType),
    }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('ALLOCATIONS_SHIPMENT_DETAILS_SAVED'),
    invalidatesTags: (result, error, arg) => [
      ...allocationTags(result, error, arg),
      { type: TAG_TYPES.OutgoingShipment, id: 'LIST' },
      { type: TAG_TYPES.Order, id: 'LIST' },
    ],
  }),

  // Step 1 of the save: the PDF the browser just rendered, before the declaration exists.
  uploadAllocationDeclarationFile: build.mutation({
    query: ({ module, id, file }) => {
      const body = new FormData();
      body.append('file', file, declarationFileName());
      return { url: PATHS.declarationFile(module, id), method: 'POST', body };
    },
    transformResponse: (dto) => dto?.output?.data?.fileId ?? null,
    transformErrorResponse: handledError,
  }),

  // Step 2: POST generates, PUT edits, and `declarationFileId` links the upload above.
  saveAllocationDeclaration: build.mutation({
    query: ({ module, id, values, declarationFileId, isEdit }) => ({
      url: PATHS.declaration(module, id),
      method: isEdit ? 'PUT' : 'POST',
      body: buildDeclarationDto(values, { declarationFileId }),
    }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('ALLOCATIONS_DECLARATION_SUCCESS'),
    invalidatesTags: allocationTags,
  }),

  // Step 3: the exact file saved above — never re-rendered, so what shipped is what downloads.
  getAllocationDeclarationFile: build.query({
    query: ({ module, id }) => ({
      url: PATHS.declarationFile(module, id),
      responseHandler: (response) => response.blob(),
    }),
    transformErrorResponse: handledError,
    providesTags: (result, error, arg) => [{ type: TAG_TYPES.Allocation, id: arg.id }],
  }),

  // One combined declaration for the ticked rows; the server re-checks that they all agree.
  bulkGenerateAllocationDeclarations: build.mutation({
    query: ({ module, transactionIds }) => ({
      url: PATHS.bulkGenerateDeclarations(module),
      method: 'POST',
      body: { transactionIds },
    }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('ALLOCATIONS_DECLARATION_SUCCESS'),
    invalidatesTags: () => [{ type: TAG_TYPES.Allocation, id: 'LIST' }],
  }),
});
